import CustomModalButtons from 'components/CustomModal/buttons';
import CustomModalHeader from 'components/CustomModal/header';
import CustomModal from 'components/CustomModal';
import { useAppDispatch, useAppSelector } from 'store';
import CompanyStatus from 'components/CompanyStatus';
import { setIsRequiredInfoPopupOpen } from 'store/chartForms/chartForms.slice';
import { companyStatus } from 'utils/constants';
import ModalTextField from 'components/ModalTextField';
import { companyWebsiteField } from 'utils/constants/fields';
import RetentionTooltip from 'components/RetentionTooltip';
import { useSubmitData } from 'hooks/useSubmitData';
import { isEmail } from 'validator';
import { buildLocalData } from 'utils/helpers/buildLocalData';
import { useEffect, useState } from 'react';
import { useAuth } from 'context/AuthContext';

const RequiredInfoModal = () => {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAuth();
  const { submitData, isSubmitting } = useSubmitData(isAuthenticated);
  const { companyDetails } = useAppSelector(state => state.companyInfo);
  const revenue = useAppSelector(
    state => state.companyInfo.requiredInfo.revenue
  );
  const fieldsValues = useAppSelector(state => state.chartForms.fields);
  const isRequiredInfoPopupOpen = useAppSelector(
    state => state.chartForms.isRequiredInfoPopupOpen
  );
  const [status, setStatus] = useState<companyStatus | null>(null);
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');

  const onCompanyStatusChange = (value: companyStatus) => {
    setStatus(value);
  };

  const onEmailChange = (value: string) => {
    setEmail(value);
  };

  const onWebsiteChange = (value: string) => {
    setWebsite(value);
  };

  const onClose = () => dispatch(setIsRequiredInfoPopupOpen(false));

  const onSubmit = async () => {
    const otherInfo = {
      companyStatus: status,
      email,
      companyWebsite: website,
      companyColor: companyDetails.companyColor,
      companyLogoURL: companyDetails.companyLogoURL,
      revenue: revenue
    };
    const localData = buildLocalData(fieldsValues, otherInfo);
    await submitData(localData, onClose, { email });
  };

  const isApplyDisabled = () => {
    if (
      !status ||
      isSubmitting ||
      (status === companyStatus.PRIVATE && !isEmail(String(email)))
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (isAuthenticated) {
      setEmail(user?.idTokenClaims?.emails?.[0] || '');
    }
  }, [user, isAuthenticated]);

  return (
    <CustomModal
      isOpen={isRequiredInfoPopupOpen}
      onClose={onClose}
      className='max-w-[570px] rounded-xl overflow-hidden'
    >
      <div className='flex flex-col gap-6 py-4 px-8'>
        <CustomModalHeader
          title='Please provide a bit more information'
          description='Share your contact and company information below to overlay your data. By entering your data, you consent to the'
          descriptionParts={[
            'Share your contact and company information below to overlay your data. By entering your data, you consent to the ',
            {
              text: 'retention by ICONIQ',
              underline: true,
              bold: true,
              tooltip: true,
              tooltipId: 'retention_tooltip'
            },
            ' of your answers for future use.'
          ]}
          onClose={onClose}
        />
        <div className='flex flex-col gap-4'>
          <CompanyStatus
            currentCompanyStatus={status}
            onCompanyStatusChange={onCompanyStatusChange}
          />
          <ModalTextField
            label={`Email Address${
              status === companyStatus.PRIVATE ? '*' : ''
            }`}
            name='email'
            value={email}
            onChange={onEmailChange}
            disabled={isAuthenticated}
            placeholder='Enter your email'
          />
          <ModalTextField
            {...companyWebsiteField}
            value={website}
            onChange={onWebsiteChange}
          />
        </div>
        <CustomModalButtons
          disabled={isApplyDisabled()}
          onClose={onClose}
          handleApply={onSubmit}
          firstBtnLabel='Submit'
          firstBtnId='submit_required_info_button'
          containerClassName='!gap-4'
        />
      </div>
      <RetentionTooltip />
    </CustomModal>
  );
};

export default RequiredInfoModal;
